import {IconColourEnum} from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import {IconsEnum} from '../../../shared-modules/shared-icons/models/icons.enum';
import {ISharedModalBasic} from '../../../shared/shared-models/modals/shared-modal-basic';
import {
  DYNAMSOFT_STORE_CAP_ERROR_CODE,
  DYNAMSOFT_STORE_USAGE_UNAVAILABLE_ERROR_CODE,
  DYNAMSOFT_STORE_USER_CAP,
} from '../services/dynamsoft-licence-usage-cap.service';

export const DYNAMSOFT_LICENCE_USAGE_EXCEEDED_CODE = 120;

function errorCode(error: unknown): unknown {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return undefined;
  }
  return (error as {code?: unknown}).code;
}

function isStoreCapError(error: unknown): boolean {
  return errorCode(error) === DYNAMSOFT_STORE_CAP_ERROR_CODE;
}

function isStoreUsageUnavailableError(error: unknown): boolean {
  return errorCode(error) === DYNAMSOFT_STORE_USAGE_UNAVAILABLE_ERROR_CODE;
}

/**
 * Reads Dynamsoft's numeric error code from a thrown SDK error object.
 *
 * @param {unknown} error - Value thrown by Dynamsoft (`errorCode` or `code`).
 * @returns {number | null} The numeric code, or null when it is not present.
 */
export function getDynamsoftErrorCode(error: unknown): number | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }

  const candidate = error as {code?: unknown; errorCode?: unknown};
  if (typeof candidate.errorCode === 'number') {
    return candidate.errorCode;
  }
  if (typeof candidate.code === 'number') {
    return candidate.code;
  }

  return null;
}

/**
 * Reads a human-readable message from a thrown Dynamsoft error.
 *
 * @param {unknown} error - Value thrown by Dynamsoft or a standard Error.
 * @returns {string} The message text, or a stringified fallback.
 */
export function getDynamsoftErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as {message: unknown}).message;
    if (typeof message === 'string') {
      return message;
    }
  }

  return String(error);
}

/**
 * Returns true when the error is a Dynamsoft licence failure (expired, trial, usage limit, or similar).
 *
 * @param {unknown} error - Value thrown while initialising or capturing with Dynamsoft.
 * @returns {boolean} True when the user should see a licence-failure modal.
 */
export function isDynamsoftLicenceError(error: unknown): boolean {
  const code: number | null = getDynamsoftErrorCode(error);
  if (code === DYNAMSOFT_LICENCE_USAGE_EXCEEDED_CODE) {
    return true;
  }

  if (isStoreCapError(error) || isStoreUsageUnavailableError(error)) {
    return true;
  }

  const message: string = getDynamsoftErrorMessage(error).toLowerCase();
  return /licen[cs]e|trial|expired|usage exceeded/.test(message);
}

/**
 * Builds the confirmation-modal copy for a Dynamsoft licence failure.
 *
 * @param {unknown} error - Value thrown by Dynamsoft.
 * @returns {ISharedModalBasic} Props for `SharedModalBasicComponent`.
 */
export function buildDynamsoftLicenceFailureModalProps(error: unknown): ISharedModalBasic {
  const isStoreCapReached =
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as {code?: unknown}).code === DYNAMSOFT_STORE_CAP_ERROR_CODE;

  if (isStoreCapReached) {
    return {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Close',
      modalTitle: 'Daily Scanner Limit Reached',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: `This store has already reached its limit of ${DYNAMSOFT_STORE_USER_CAP} scanner users today.`,
      contextDescription: [
        'The barcode scanner was not started.',
        '',
        'The daily limit resets at midnight SAST.',
      ],
      cssClass: ['shared-basic-modal-css'],
    };
  }

  if (isStoreUsageUnavailableError(error)) {
    return {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Close',
      modalTitle: 'Scanner Unavailable',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'Today\'s scanner usage for this store could not be confirmed.',
      contextDescription: [
        'The barcode scanner was not started.',
        '',
        'Check that you are online and signed in to a store, then try again. A device clock that does not match the current SAST day is also rejected.',
      ],
      cssClass: ['shared-basic-modal-css'],
    };
  }

  const isUsageLimit: boolean =
    getDynamsoftErrorCode(error) === DYNAMSOFT_LICENCE_USAGE_EXCEEDED_CODE ||
    getDynamsoftErrorMessage(error).toLowerCase().includes('usage exceeded');

  if (isUsageLimit) {
    return {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Close',
      modalTitle: 'Scanner Licence Limit Reached',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'The barcode scanner licence usage limit has been reached.',
      contextDescription: [
        'The barcode scanner is currently unavailable because the scanner licence usage limit has been reached.',
        '',
        'Please contact Gallix Support to resolve this issue.',
      ],
      cssClass: ['shared-basic-modal-css'],
    };
  }

  return {
    buttonAccept: false,
    buttonAcceptText: '',
    buttonClose: true,
    buttonCloseCross: false,
    buttonCloseText: 'Close',
    modalTitle: 'Scanner Licence Unavailable',
    modalTitleIcon: IconsEnum.infoSlabCircleOutline,
    modalTitleIconColor: IconColourEnum.red,
    contextHeading: 'The barcode scanner licence could not be verified.',
    contextDescription: [
      'The barcode scanner is currently unavailable because the scanner licence has expired or is not valid.',
      '',
      'Please contact Gallix Support to resolve this issue.',
    ],
    cssClass: ['shared-basic-modal-css'],
  };
}
